import React from "react";
import {
  Badge,
  Button,
  Card,
  CardBody,
  CardHeader,
  Col,
  Container,
  FormGroup,
  Input,
  InputGroup,
  Modal,
  ModalBody,
  ModalFooter,
  ModalHeader,
  Row,
  Spinner,
  Table,
} from "reactstrap";
import Header from "../../components/Headers/Header.js";
import { axiosDelete, axiosGet, axiosPost } from "../../util/apiConfig";
import { CSVLink } from "react-csv";
import moment from "moment";

const CLASS_OPTIONS = [
  { value: "", label: "All Classes" },
  { value: "CLASS1648760926141", label: "Chess" },
  { value: "CLASS1688966054748", label: "Coding" },
  { value: "CLASS1695200316932", label: "Math" },
  { value: "CLASS1784904166957", label: "ELA Club" },
];

const DATE_PRESETS = [
  { value: "7", label: "Last 7 Days" },
  { value: "30", label: "Last 30 Days" },
  { value: "month", label: "This Month" },
  { value: "all", label: "All Time" },
  { value: "custom", label: "Custom Range" },
];

const StickyTh = ({ children, className = "" }) => (
  <th
    scope="col"
    className={`text-center ${className}`}
    style={{ position: "sticky", top: 0, background: "#f6f9fc", zIndex: 1, whiteSpace: "nowrap" }}
  >
    {children}
  </th>
);

/* ─── Inline payment picker shown below each unmatched trial row ─── */
class PaymentPicker extends React.Component {
  constructor(props) {
    super(props);
    this.state = {
      search: "",
      payments: [],
      loading: false,
      loaded: false,
    };
    this.searchTimer = null;
  }

  componentDidMount() {
    this.fetchPayments("");
  }

  fetchPayments = (search) => {
    this.setState({ loading: true });
    axiosGet(`sales/recent-payments${search ? `?search=${encodeURIComponent(search)}` : ""}`)
      .then((res) => {
        this.setState({
          loading: false,
          loaded: true,
          payments: (!res.isError && Array.isArray(res.data)) ? res.data : [],
        });
      })
      .catch(() => this.setState({ loading: false, loaded: true, payments: [] }));
  };

  handleSearch = (e) => {
    const val = e.target.value;
    this.setState({ search: val });
    clearTimeout(this.searchTimer);
    this.searchTimer = setTimeout(() => this.fetchPayments(val), 400);
  };

  render() {
    const { onSelect, trialEmail } = this.props;
    const { search, payments, loading, loaded } = this.state;

    return (
      <div
        style={{
          border: "1px solid #dee2e6",
          borderRadius: "6px",
          background: "#fff",
          boxShadow: "0 4px 12px rgba(0,0,0,0.1)",
          overflow: "hidden",
        }}
      >
        {/* Search box */}
        <div style={{ padding: "8px", borderBottom: "1px solid #f0f0f0", background: "#f8f9fa" }}>
          <Input
            type="text"
            bsSize="sm"
            placeholder="Search by name or email…"
            value={search}
            onChange={this.handleSearch}
            autoFocus
          />
        </div>

        {/* Payment list */}
        <div style={{ maxHeight: "200px", overflowY: "auto" }}>
          {loading && (
            <div className="text-center py-3">
              <Spinner size="sm" color="primary" />
              <span className="ml-2 text-muted" style={{ fontSize: "0.8rem" }}>Loading payments…</span>
            </div>
          )}
          {!loading && loaded && payments.length === 0 && (
            <div className="text-muted text-center py-3" style={{ fontSize: "0.8rem" }}>
              No payments found{search ? ` for "${search}"` : ""}.
            </div>
          )}
          {!loading && payments.map((p) => {
            const isSameEmail = p.email.toLowerCase() === trialEmail.toLowerCase();
            return (
              <div
                key={p.email}
                onClick={() => !isSameEmail && onSelect(p)}
                style={{
                  padding: "7px 12px",
                  borderBottom: "1px solid #f5f5f5",
                  cursor: isSameEmail ? "not-allowed" : "pointer",
                  background: isSameEmail ? "#fafafa" : undefined,
                  opacity: isSameEmail ? 0.5 : 1,
                  transition: "background 0.15s",
                }}
                onMouseEnter={(e) => { if (!isSameEmail) e.currentTarget.style.background = "#e8f4ff"; }}
                onMouseLeave={(e) => { if (!isSameEmail) e.currentTarget.style.background = ""; }}
                title={isSameEmail ? "Same as trial email — choose a different one" : `Select ${p.email}`}
              >
                <div style={{ fontSize: "0.82rem", fontWeight: 600, color: "#344767" }}>
                  {p.name || <em className="text-muted">No name</em>}
                </div>
                <div style={{ fontSize: "0.75rem", color: "#6c757d" }}>{p.email}</div>
                {p.lastPayment && (
                  <div style={{ fontSize: "0.7rem", color: "#adb5bd" }}>
                    {moment(p.lastPayment).format("DD MMM YYYY")}
                  </div>
                )}
              </div>
            );
          })}
        </div>

        <div style={{ padding: "6px 10px", background: "#f8f9fa", borderTop: "1px solid #f0f0f0" }}>
          <small className="text-muted" style={{ fontSize: "0.72rem" }}>
            Showing up to 50 recent unique payers · type to filter
          </small>
        </div>
      </div>
    );
  }
}

class DemoTeacherDashboard extends React.Component {
  constructor(props) {
    super(props);
    const today = moment().format("YYYY-MM-DD");
    const sevenDaysAgo = moment().subtract(7, "days").format("YYYY-MM-DD");

    this.state = {
      teachers: [],
      totals: {},
      loading: false,
      filterFromDate: sevenDaysAgo,
      filterToDate: today,
      classId: "",
      datePreset: "7",
      expandedRow: null,

      // Link modal
      linkModal: false,
      linkTeacher: null,
      linkLoading: false,
      unconverted: [],
      existingAliases: [],
      // notificationId → { email, name } of the selected payment
      selectedPayments: {},
      // notificationId → true while saving
      linkSaving: {},
      // notificationId → true when picker is open
      pickerOpen: {},
    };
  }

  componentDidMount() {
    this.fetchPerformanceData();
  }

  handleDatePreset = (preset) => {
    const today = moment().format("YYYY-MM-DD");
    if (preset === "all") {
      this.setState({ datePreset: "all", filterFromDate: "", filterToDate: "" }, () =>
        this.fetchPerformanceData()
      );
      return;
    }
    if (preset === "custom") {
      this.setState({ datePreset: "custom" });
      return;
    }
    const fromMap = {
      "7": moment().subtract(7, "days").format("YYYY-MM-DD"),
      "30": moment().subtract(30, "days").format("YYYY-MM-DD"),
      month: moment().startOf("month").format("YYYY-MM-DD"),
    };
    this.setState(
      { datePreset: preset, filterFromDate: fromMap[preset], filterToDate: today },
      () => this.fetchPerformanceData()
    );
  };

  fetchPerformanceData = () => {
    const { filterFromDate, filterToDate, classId } = this.state;
    let url = "sales/demo-teacher-performance?";
    const params = [];
    if (filterFromDate) params.push(`fromDate=${filterFromDate}`);
    if (filterToDate) params.push(`toDate=${filterToDate}`);
    if (classId) params.push(`classId=${classId}`);
    url += params.join("&");

    this.setState({ loading: true });
    axiosGet(url)
      .then((response) => {
        if (!response.isError && response.data) {
          this.setState({
            loading: false,
            teachers: response.data.teachers || [],
            totals: response.data.totals || {},
          });
        } else {
          this.setState({ loading: false, teachers: [], totals: {} });
        }
      })
      .catch(() => this.setState({ loading: false, teachers: [], totals: {} }));
  };

  getCSVData = () =>
    this.state.teachers.map((t) => ({
      Teacher: t.teacher || "",
      "Total Trials": t.totalTrials || 0,
      "Very Interested": t.veryInterested || 0,
      "Medium Interested": t.mediumInterested || 0,
      "Less Interested": t.lessInterested || 0,
      "Teacher No Show": t.teacherNoShow || 0,
      "Student No Show": t.studentNoShow || 0,
      Cancelled: t.cancelClass || 0,
      Default: t.defaultStatus || 0,
      "Completed Trials": t.completedTrials || 0,
      Conversions: t.conversions || 0,
      "Conversion %": t.conversionPercentage || 0,
    }));

  toggleExpandRow = (index) =>
    this.setState((prev) => ({ expandedRow: prev.expandedRow === index ? null : index }));

  getConversionBadgeColor = (pct) => {
    if (pct >= 20) return "success";
    if (pct >= 10) return "warning";
    return "danger";
  };

  /* ─── Link modal ─── */

  openLinkModal = (teacher) => {
    const { filterFromDate, filterToDate, classId } = this.state;
    this.setState({
      linkModal: true,
      linkTeacher: teacher,
      linkLoading: true,
      unconverted: [],
      existingAliases: [],
      selectedPayments: {},
      linkSaving: {},
      pickerOpen: {},
    });

    let url = `sales/unconverted-trials?teacher=${encodeURIComponent(teacher)}`;
    if (filterFromDate) url += `&fromDate=${filterFromDate}`;
    if (filterToDate) url += `&toDate=${filterToDate}`;
    if (classId) url += `&classId=${classId}`;

    axiosGet(url)
      .then((res) => {
        if (!res.isError && res.data) {
          this.setState({
            linkLoading: false,
            unconverted: res.data.unconverted || [],
            existingAliases: res.data.aliases || [],
          });
        } else {
          this.setState({ linkLoading: false });
        }
      })
      .catch(() => this.setState({ linkLoading: false }));
  };

  closeLinkModal = () => this.setState({ linkModal: false, linkTeacher: null });

  togglePicker = (notificationId) =>
    this.setState((prev) => ({
      pickerOpen: {
        ...prev.pickerOpen,
        [notificationId]: !prev.pickerOpen[notificationId],
      },
    }));

  selectPayment = (notificationId, payment) =>
    this.setState((prev) => ({
      selectedPayments: { ...prev.selectedPayments, [notificationId]: payment },
      pickerOpen: { ...prev.pickerOpen, [notificationId]: false },
    }));

  clearSelection = (notificationId) =>
    this.setState((prev) => ({
      selectedPayments: { ...prev.selectedPayments, [notificationId]: null },
    }));

  linkTrialToPayment = (trial) => {
    const { selectedPayments, linkTeacher } = this.state;
    const selected = selectedPayments[trial.notificationId];
    if (!selected) {
      alert("Select a payment from the list first.");
      return;
    }
    if (selected.email.toLowerCase() === trial.email.toLowerCase()) {
      alert("Payment email must be different from the trial email.");
      return;
    }

    this.setState((prev) => ({
      linkSaving: { ...prev.linkSaving, [trial.notificationId]: true },
    }));

    axiosPost("sales/trial-email-aliases", {
      trialEmail: trial.email,
      paymentEmail: selected.email,
      paymentName: selected.name || "",
      notificationId: trial.notificationId,
      teacherName: linkTeacher,
    })
      .then((res) => {
        this.setState((prev) => ({
          linkSaving: { ...prev.linkSaving, [trial.notificationId]: false },
        }));
        if (!res.isError) {
          const isNew = res.status === 201;
          const newAlias = res.data;
          this.setState((prev) => {
            const already = prev.existingAliases.some((a) => a.aliasId === newAlias.aliasId);
            return {
              existingAliases: already ? prev.existingAliases : [newAlias, ...prev.existingAliases],
              unconverted: isNew
                ? prev.unconverted.filter((t) => t.notificationId !== trial.notificationId)
                : prev.unconverted,
              selectedPayments: { ...prev.selectedPayments, [trial.notificationId]: null },
            };
          });
          this.fetchPerformanceData();
        }
      })
      .catch(() =>
        this.setState((prev) => ({
          linkSaving: { ...prev.linkSaving, [trial.notificationId]: false },
        }))
      );
  };

  unlinkAlias = (aliasId) => {
    axiosDelete(`sales/trial-email-aliases/${aliasId}`)
      .then((res) => {
        if (!res.isError) {
          this.setState((prev) => ({
            existingAliases: prev.existingAliases.filter((a) => a.aliasId !== aliasId),
          }));
          this.fetchPerformanceData();
        }
      })
      .catch(() => {});
  };

  render() {
    const {
      teachers,
      totals,
      loading,
      filterFromDate,
      filterToDate,
      classId,
      datePreset,
      expandedRow,
      linkModal,
      linkTeacher,
      linkLoading,
      unconverted,
      existingAliases,
      selectedPayments,
      linkSaving,
      pickerOpen,
    } = this.state;

    return (
      <>
        <Header />
        <Container className="mt--7" fluid>

          {/* ─── Summary Cards ─── */}
          <Row className="mb-4">
            {[
              { label: "Total Trials", value: totals.totalTrials || 0, icon: "ni-chart-bar-32", color: "orange" },
              { label: "Completed", value: totals.completedTrials || 0, icon: "ni-check-bold", color: "green" },
              { label: "Conversions", value: totals.conversions || 0, icon: "ni-money-coins", color: "info" },
              { label: "Conversion %", value: `${totals.conversionPercentage || 0}%`, icon: "ni-chart-pie-35", color: "red" },
            ].map((card) => (
              <Col key={card.label} lg="3" md="6" className="mb-3">
                <Card className="card-stats shadow">
                  <CardBody>
                    <Row>
                      <div className="col">
                        <h5 className="card-title text-uppercase text-muted mb-0">{card.label}</h5>
                        <span className="h2 font-weight-bold mb-0">
                          {loading ? "..." : card.value}
                        </span>
                      </div>
                      <Col className="col-auto">
                        <div className={`icon icon-shape bg-gradient-${card.color} text-white rounded-circle shadow`}>
                          <i className={`ni ${card.icon}`} />
                        </div>
                      </Col>
                    </Row>
                  </CardBody>
                </Card>
              </Col>
            ))}
          </Row>

          {/* ─── Main Table ─── */}
          <Row>
            <div className="col">
              <Card className="shadow">
                <CardHeader className="border-0">
                  <Row className="align-items-center">
                    <Col xs="12" md="4">
                      <h3 className="mb-0">Demo Teacher Performance</h3>
                      <small className="text-muted">
                        {filterFromDate && filterToDate
                          ? `${moment(filterFromDate).format("MMM DD, YYYY")} — ${moment(filterToDate).format("MMM DD, YYYY")}`
                          : "All Time"}
                        {" · "}
                        <span className="text-primary font-weight-bold">sorted by Conversion %</span>
                      </small>
                    </Col>

                    <Col xs="12" md="8">
                      <div className="d-flex flex-wrap align-items-center justify-content-md-end" style={{ gap: "10px", marginTop: "10px" }}>
                        <div className="btn-group btn-group-sm" role="group">
                          {DATE_PRESETS.map((preset) => (
                            <Button
                              key={preset.value}
                              color={datePreset === preset.value ? "primary" : "secondary"}
                              size="sm"
                              onClick={() => this.handleDatePreset(preset.value)}
                              outline={datePreset !== preset.value}
                            >
                              {preset.label}
                            </Button>
                          ))}
                        </div>

                        {datePreset === "custom" && (
                          <FormGroup className="mb-0 d-flex align-items-center" style={{ gap: "5px" }}>
                            <InputGroup style={{ width: "140px" }} className="input-group-alternative">
                              <Input
                                type="date"
                                placeholder="From"
                                value={filterFromDate}
                                max={moment().format("YYYY-MM-DD")}
                                onChange={(e) =>
                                  this.setState({ filterFromDate: e.target.value }, () => {
                                    if (this.state.filterToDate) this.fetchPerformanceData();
                                  })
                                }
                              />
                            </InputGroup>
                            <small className="text-muted">to</small>
                            <InputGroup style={{ width: "140px" }} className="input-group-alternative">
                              <Input
                                type="date"
                                placeholder="To"
                                value={filterToDate}
                                max={moment().format("YYYY-MM-DD")}
                                onChange={(e) =>
                                  this.setState({ filterToDate: e.target.value }, () => {
                                    if (this.state.filterFromDate) this.fetchPerformanceData();
                                  })
                                }
                              />
                            </InputGroup>
                          </FormGroup>
                        )}

                        <Input
                          type="select"
                          bsSize="sm"
                          style={{ width: "140px" }}
                          value={classId}
                          onChange={(e) =>
                            this.setState({ classId: e.target.value }, () => this.fetchPerformanceData())
                          }
                        >
                          {CLASS_OPTIONS.map((opt) => (
                            <option key={opt.value} value={opt.value}>{opt.label}</option>
                          ))}
                        </Input>

                        {teachers.length > 0 && (
                          <CSVLink
                            data={this.getCSVData()}
                            filename={`demo-teacher-performance-${filterFromDate || "all"}-to-${filterToDate || "time"}.csv`}
                            className="btn btn-sm btn-success"
                          >
                            <i className="ni ni-cloud-download-95 mr-1" />
                            Export CSV
                          </CSVLink>
                        )}
                      </div>
                    </Col>
                  </Row>
                </CardHeader>

                <CardBody style={{ padding: 0 }}>
                  <div style={{ overflowX: "auto", maxHeight: "600px", overflowY: "auto" }}>
                    <Table className="align-items-center table-flush" responsive hover size="sm">
                      <thead className="thead-light">
                        <tr>
                          <StickyTh className="text-left">#</StickyTh>
                          <StickyTh className="text-left">Teacher</StickyTh>
                          <StickyTh>Total Trials</StickyTh>
                          <StickyTh>Very Int.</StickyTh>
                          <StickyTh>Med Int.</StickyTh>
                          <StickyTh>Less Int.</StickyTh>
                          <StickyTh>T. No Show</StickyTh>
                          <StickyTh>S. No Show</StickyTh>
                          <StickyTh>Cancel</StickyTh>
                          <StickyTh>Default</StickyTh>
                          <StickyTh>Completed</StickyTh>
                          <StickyTh>Conversions</StickyTh>
                          <StickyTh>
                            Conv. %{" "}
                            <i className="ni ni-bold-down" style={{ fontSize: "0.6rem" }} title="Sorted highest first" />
                          </StickyTh>
                          <StickyTh>Link</StickyTh>
                        </tr>
                      </thead>
                      <tbody>
                        {loading ? (
                          <tr>
                            <td colSpan={14} className="text-center py-5">
                              <Spinner color="primary" size="sm" />
                              <span className="ml-2">Loading performance data...</span>
                            </td>
                          </tr>
                        ) : teachers.length > 0 ? (
                          <>
                            {teachers.map((teacher, index) => (
                              <React.Fragment key={index}>
                                <tr>
                                  <td>{index + 1}</td>
                                  <td>
                                    <span className="font-weight-bold">{teacher.teacher || "Unknown"}</span>
                                  </td>
                                  <td className="text-center font-weight-bold">{teacher.totalTrials}</td>
                                  <td className="text-center">
                                    {teacher.veryInterested > 0 ? <Badge color="success" pill>{teacher.veryInterested}</Badge> : <span className="text-muted">0</span>}
                                  </td>
                                  <td className="text-center">
                                    {teacher.mediumInterested > 0 ? <Badge color="info" pill>{teacher.mediumInterested}</Badge> : <span className="text-muted">0</span>}
                                  </td>
                                  <td className="text-center">
                                    {teacher.lessInterested > 0 ? <Badge color="warning" pill>{teacher.lessInterested}</Badge> : <span className="text-muted">0</span>}
                                  </td>
                                  <td className="text-center">
                                    {teacher.teacherNoShow > 0 ? <Badge color="danger" pill>{teacher.teacherNoShow}</Badge> : <span className="text-muted">0</span>}
                                  </td>
                                  <td className="text-center">
                                    {teacher.studentNoShow > 0 ? <Badge color="danger" pill>{teacher.studentNoShow}</Badge> : <span className="text-muted">0</span>}
                                  </td>
                                  <td className="text-center">
                                    {teacher.cancelClass > 0
                                      ? <Badge color="" style={{ backgroundColor: "#adb5bd", color: "#fff" }} pill>{teacher.cancelClass}</Badge>
                                      : <span className="text-muted">0</span>}
                                  </td>
                                  <td className="text-center">
                                    {teacher.defaultStatus > 0 ? teacher.defaultStatus : <span className="text-muted">0</span>}
                                  </td>
                                  <td className="text-center font-weight-bold">{teacher.completedTrials}</td>
                                  <td className="text-center">
                                    {teacher.conversions > 0 ? (
                                      <span
                                        className="font-weight-bold text-primary"
                                        style={{ cursor: "pointer" }}
                                        onClick={() => this.toggleExpandRow(index)}
                                        title="Click to see converted emails"
                                      >
                                        {teacher.conversions}{" "}
                                        <i className={`ni ni-bold-${expandedRow === index ? "up" : "down"}`} style={{ fontSize: "0.65rem" }} />
                                      </span>
                                    ) : (
                                      <span className="font-weight-bold text-primary">0</span>
                                    )}
                                  </td>
                                  <td className="text-center">
                                    <Badge color={this.getConversionBadgeColor(teacher.conversionPercentage)} className="px-3">
                                      {teacher.conversionPercentage}%
                                    </Badge>
                                  </td>
                                  <td className="text-center">
                                    <Button
                                      color="warning"
                                      size="sm"
                                      outline
                                      onClick={() => this.openLinkModal(teacher.teacher)}
                                      title="Link a trial email to a different payment"
                                      style={{ whiteSpace: "nowrap" }}
                                    >
                                      <i className="ni ni-link-68 mr-1" />
                                      Link
                                    </Button>
                                  </td>
                                </tr>

                                {expandedRow === index && teacher.convertedEmails && teacher.convertedEmails.length > 0 && (
                                  <tr style={{ backgroundColor: "#f0f8ff" }}>
                                    <td colSpan={14} className="py-2 px-4">
                                      <small className="text-muted font-weight-bold d-block mb-1">
                                        Converted emails ({teacher.convertedEmails.length}):
                                      </small>
                                      <div className="d-flex flex-wrap" style={{ gap: "6px" }}>
                                        {teacher.convertedEmails.map((email, i) => (
                                          <Badge key={i} color="light" className="border text-dark" style={{ fontSize: "0.8rem" }}>
                                            {email}
                                          </Badge>
                                        ))}
                                      </div>
                                    </td>
                                  </tr>
                                )}
                              </React.Fragment>
                            ))}

                            <tr style={{ backgroundColor: "#f6f9fc", fontWeight: "bold", borderTop: "2px solid #dee2e6" }}>
                              <td></td>
                              <td><strong>TOTAL</strong></td>
                              <td className="text-center">{totals.totalTrials}</td>
                              <td className="text-center">{totals.veryInterested}</td>
                              <td className="text-center">{totals.mediumInterested}</td>
                              <td className="text-center">{totals.lessInterested}</td>
                              <td className="text-center">{totals.teacherNoShow}</td>
                              <td className="text-center">{totals.studentNoShow}</td>
                              <td className="text-center">{totals.cancelClass}</td>
                              <td className="text-center">{totals.defaultStatus}</td>
                              <td className="text-center">{totals.completedTrials}</td>
                              <td className="text-center text-primary">{totals.conversions}</td>
                              <td className="text-center">
                                <Badge color={this.getConversionBadgeColor(totals.conversionPercentage || 0)} className="px-3">
                                  {totals.conversionPercentage || 0}%
                                </Badge>
                              </td>
                              <td></td>
                            </tr>
                          </>
                        ) : (
                          <tr>
                            <td colSpan={14} className="text-center py-5 text-muted">
                              <i className="ni ni-chart-pie-35" style={{ fontSize: "2rem" }} />
                              <br />
                              No performance data found for the selected range.
                            </td>
                          </tr>
                        )}
                      </tbody>
                    </Table>
                  </div>
                </CardBody>
              </Card>
            </div>
          </Row>
        </Container>

        {/* ─── Link Trial Modal ─── */}
        <Modal isOpen={linkModal} toggle={this.closeLinkModal} size="lg">
          <ModalHeader toggle={this.closeLinkModal}>
            Link Trial → Payment
            {linkTeacher && (
              <small className="d-block text-muted font-weight-normal" style={{ fontSize: "0.8rem" }}>
                Teacher: <strong>{linkTeacher}</strong>
              </small>
            )}
          </ModalHeader>

          <ModalBody style={{ maxHeight: "72vh", overflowY: "auto", padding: "16px 20px" }}>
            {linkLoading ? (
              <div className="text-center py-5">
                <Spinner color="primary" />
                <p className="mt-2 text-muted">Loading unmatched trials…</p>
              </div>
            ) : (
              <>
                {/* ── Unmatched trials ── */}
                <p className="text-uppercase text-muted mb-2" style={{ fontSize: "0.7rem", letterSpacing: "0.06em", fontWeight: 600 }}>
                  Unmatched completed trials ({unconverted.length})
                </p>

                {unconverted.length === 0 ? (
                  <div className="text-center py-3 border rounded mb-3 text-muted" style={{ fontSize: "0.88rem" }}>
                    <i className="ni ni-check-bold text-success mr-1" />
                    All completed trials are already matched to a payment.
                  </div>
                ) : (
                  <div className="mb-4">
                    {unconverted.map((trial) => {
                      const sel = selectedPayments[trial.notificationId];
                      const isPickerOpen = !!pickerOpen[trial.notificationId];
                      const isSaving = !!linkSaving[trial.notificationId];

                      return (
                        <div
                          key={trial.notificationId}
                          style={{
                            border: "1px solid #dee2e6",
                            borderRadius: "6px",
                            marginBottom: "10px",
                            overflow: "hidden",
                          }}
                        >
                          {/* ── Trial info + actions ── */}
                          <Row
                            className="align-items-center mx-0"
                            style={{ background: "#f8f9fa", padding: "8px 10px", borderBottom: "1px solid #e9ecef" }}
                          >
                            <Col xs="5" style={{ paddingLeft: "4px", paddingRight: "4px" }}>
                              <div style={{ fontWeight: 600, fontSize: "0.84rem" }}>{trial.name || "—"}</div>
                              <div style={{ fontSize: "0.73rem", color: "#6c757d", wordBreak: "break-all" }}>{trial.email}</div>
                            </Col>
                            <Col xs="4" style={{ paddingLeft: "4px", paddingRight: "4px" }}>
                              <Badge
                                color={
                                  trial.status === "Very Interested"
                                    ? "success"
                                    : trial.status === "Medium Interested"
                                    ? "info"
                                    : "warning"
                                }
                                pill
                                style={{ fontSize: "0.68rem" }}
                              >
                                {trial.status}
                              </Badge>
                              {trial.date && (
                                <div style={{ fontSize: "0.7rem", color: "#adb5bd", marginTop: "2px" }}>
                                  {moment(trial.date).format("DD MMM YY")}
                                </div>
                              )}
                            </Col>
                            <Col xs="3" className="text-right" style={{ paddingLeft: "4px", paddingRight: "4px" }}>
                              <Button
                                color={isPickerOpen ? "secondary" : "info"}
                                size="sm"
                                onClick={() => this.togglePicker(trial.notificationId)}
                              >
                                {isPickerOpen ? "Close" : "Browse"}
                              </Button>
                            </Col>
                          </Row>

                          {/* ── Selected payment + Link button ── */}
                          <Row
                            className="align-items-center mx-0"
                            style={{ padding: "7px 10px", background: sel ? "#f0fff4" : "#fff" }}
                          >
                            <Col style={{ paddingLeft: "4px", paddingRight: "4px" }}>
                              {sel ? (
                                <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
                                  <div>
                                    <div style={{ fontWeight: 600, fontSize: "0.82rem", color: "#155724" }}>
                                      {sel.name || <em className="text-muted">No name</em>}
                                    </div>
                                    <div style={{ fontSize: "0.73rem", color: "#1e7e34", wordBreak: "break-all" }}>{sel.email}</div>
                                  </div>
                                  <button
                                    onClick={() => this.clearSelection(trial.notificationId)}
                                    title="Clear"
                                    style={{ background: "none", border: "none", cursor: "pointer", color: "#721c24", fontSize: "1.1rem", lineHeight: 1, padding: "0 4px", flexShrink: 0 }}
                                  >
                                    ×
                                  </button>
                                </div>
                              ) : (
                                <span style={{ color: "#adb5bd", fontStyle: "italic", fontSize: "0.78rem" }}>
                                  Click Browse to pick a payment
                                </span>
                              )}
                            </Col>
                            <Col xs="auto" style={{ paddingLeft: "4px", paddingRight: "4px" }}>
                              <Button
                                color="primary"
                                size="sm"
                                disabled={!sel || isSaving}
                                onClick={() => this.linkTrialToPayment(trial)}
                              >
                                {isSaving ? <Spinner size="sm" /> : "Link"}
                              </Button>
                            </Col>
                          </Row>

                          {/* ── Payment picker ── */}
                          {isPickerOpen && (
                            <div style={{ borderTop: "1px solid #e9ecef", padding: "8px 10px" }}>
                              <PaymentPicker
                                trialEmail={trial.email}
                                onSelect={(payment) => this.selectPayment(trial.notificationId, payment)}
                              />
                            </div>
                          )}
                        </div>
                      );
                    })}
                  </div>
                )}

                {/* ── Existing links ── */}
                <p className="text-uppercase text-muted mb-2" style={{ fontSize: "0.7rem", letterSpacing: "0.06em", fontWeight: 600 }}>
                  Existing links ({existingAliases.length})
                </p>

                {existingAliases.length === 0 ? (
                  <div className="text-center py-2 border rounded text-muted" style={{ fontSize: "0.85rem" }}>
                    No manual links created yet.
                  </div>
                ) : (
                  existingAliases.map((alias) => (
                    <div
                      key={alias.aliasId}
                      style={{
                        border: "1px solid #dee2e6",
                        borderRadius: "6px",
                        marginBottom: "8px",
                        padding: "8px 12px",
                        background: "#fff",
                      }}
                    >
                      <Row className="align-items-center mx-0">
                        <Col style={{ paddingLeft: 0 }}>
                          <div style={{ fontSize: "0.78rem", color: "#6c757d", marginBottom: "2px" }}>
                            Trial: <code style={{ fontSize: "0.78rem" }}>{alias.trialEmail}</code>
                          </div>
                          <div style={{ fontSize: "0.78rem", color: "#155724" }}>
                            Payment:{" "}
                            {alias.paymentName && <strong>{alias.paymentName} — </strong>}
                            <code style={{ fontSize: "0.78rem" }}>{alias.paymentEmail}</code>
                          </div>
                        </Col>
                        <Col xs="auto" className="text-right" style={{ paddingRight: 0 }}>
                          <div style={{ fontSize: "0.7rem", color: "#adb5bd", marginBottom: "4px", textAlign: "right" }}>
                            {alias.createdAt ? moment(alias.createdAt).format("DD MMM YY") : ""}
                          </div>
                          <Button
                            color="danger"
                            size="sm"
                            outline
                            onClick={() => {
                              if (window.confirm("Remove this link? Conversion count will be recalculated.")) {
                                this.unlinkAlias(alias.aliasId);
                              }
                            }}
                          >
                            Remove
                          </Button>
                        </Col>
                      </Row>
                    </div>
                  ))
                )}
              </>
            )}
          </ModalBody>

          <ModalFooter style={{ padding: "10px 20px" }}>
            <small className="text-muted mr-auto" style={{ fontSize: "0.76rem" }}>
              Linking recalculates conversions — table refreshes automatically.
            </small>
            <Button color="secondary" size="sm" onClick={this.closeLinkModal}>Close</Button>
          </ModalFooter>
        </Modal>
      </>
    );
  }
}

export default DemoTeacherDashboard;
